export const CABIN_CREW_AOC_CODES = new Set(["cam"]);
export const GROUP_ADMIN_AOC_CODES = new Set(["maa", "aax", "taa", "tax", "iaa", "paa", "aac"]);

const FLIGHT_FORM_TYPE_CODES = ["ts_1", "ets_3", "ets_5", "ets_6"];
const CABIN_CREW_FORM_TYPE_CODES = ["cabin_crew", "cabin_crew_instructor"];

type AocOption = { id?: string; code?: string; description?: string; value?: string };

export function buildAocFilterOptions(aocs: AocOption[] | undefined): Array<{ label: string; value: string }> {
	return (aocs ?? []).flatMap((aoc) => {
		const value = aoc.id?.trim();
		const code = (aoc.code || aoc.description || "").trim().toLowerCase();
		if (!value || !GROUP_ADMIN_AOC_CODES.has(code)) {
			return [];
		}

		return [
			{
				label: (aoc.description || aoc.code || "").trim().toUpperCase(),
				value,
			},
		];
	});
}

export function resolveAocCodeById(aocs: AocOption[] | undefined, aocId?: string): string | undefined {
	const trimmed = aocId?.trim();
	if (!trimmed) {
		return undefined;
	}

	const match = (aocs ?? []).find((aoc) => (aoc.id ?? aoc.value ?? "") === trimmed);
	return match?.code?.toLowerCase();
}

export function getFormTypeCodesForAoc(aocCode?: string): string[] {
	if (!aocCode?.trim()) {
		return [];
	}

	if (CABIN_CREW_AOC_CODES.has(aocCode.toLowerCase())) {
		return CABIN_CREW_FORM_TYPE_CODES;
	}

	return FLIGHT_FORM_TYPE_CODES;
}

export function filterOptionsForAoc<T extends { value: string }>(
	allOptions: T[],
	aocId: string | undefined,
	aocs: AocOption[] | undefined,
): T[] {
	const allowed = new Set(getFormTypeCodesForAoc(resolveAocCodeById(aocs, aocId)));
	if (!allowed.size) {
		return [];
	}

	return allOptions.filter((option) => allowed.has(option.value));
}

export function pruneFormTypeSelection<T extends string>(
	selected: T[] | undefined,
	allowedOptions: Array<{ value: string }>,
): T[] {
	const allowed = new Set(allowedOptions.map((option) => option.value));
	return (selected ?? []).filter((value) => allowed.has(value));
}

export function parseAocIdsFromQuery(value: string | string[] | undefined | null): string[] {
	if (!value) {
		return [];
	}

	if (Array.isArray(value)) {
		return value.map((item) => item.trim()).filter(Boolean);
	}

	return value
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean);
}

export function pruneFormTypeValue(selected: string | undefined, allowedOptions: Array<{ value: string }>): string {
	const trimmed = selected?.trim() ?? "";
	if (!trimmed) {
		return "";
	}

	const allowed = new Set(allowedOptions.map((option) => option.value));
	return allowed.has(trimmed) ? trimmed : "";
}
